import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface SESStatus {
  domain: 'pending' | 'verified' | 'failed' | 'checking';
  email: 'pending' | 'verified' | 'failed' | 'checking';
  productionAccess: 'pending' | 'approved' | 'rejected' | 'checking' | 'failed';
  lastChecked: Date | null;
  error?: string;
}

const SESRealTimeChecker: React.FC = () => {
  const [status, setStatus] = useState<SESStatus>({
    domain: 'pending',
    email: 'pending',
    productionAccess: 'pending',
    lastChecked: null
  });
  const [isChecking, setIsChecking] = useState(false);

  const checkSESStatus = async () => {
    setIsChecking(true);
    setStatus(prev => ({
      ...prev,
      domain: 'checking',
      email: 'checking',
      productionAccess: 'checking',
      lastChecked: new Date()
    }));

    try {
      // Simulate API call to check SES status
      await new Promise(resolve => setTimeout(resolve, 3000));

      // For now, simulate different statuses
      // In production, this would call AWS SES API
      const mockStatus: SESStatus = {
        domain: 'verified', // Change this based on actual status
        email: 'verified',  // Change this based on actual status
        productionAccess: 'pending', // This usually takes 24-48 hours
        lastChecked: new Date()
      };

      setStatus(mockStatus);
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        domain: 'failed',
        email: 'failed',
        productionAccess: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-check every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isChecking) {
        checkSESStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isChecking]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
      case 'rejected':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'checking':
        return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'approved':
        return 'Approved';
      case 'failed':
        return 'Failed';
      case 'rejected':
        return 'Rejected';
      case 'checking':
        return 'Checking...';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'failed':
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'checking':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getOverallStatus = () => {
    if (status.domain === 'verified' && status.email === 'verified') {
      return 'ready';
    } else if (status.domain === 'failed' || status.email === 'failed') {
      return 'error';
    } else {
      return 'pending';
    }
  };

  const overallStatus = getOverallStatus();

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">AWS SES Real-Time Status</h3>
          <p className="text-sm text-gray-600">
            {status.lastChecked && `Last checked: ${status.lastChecked.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={checkSESStatus}
          disabled={isChecking}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <ArrowPathIcon className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
          <span>{isChecking ? 'Checking...' : 'Check Now'}</span>
        </button>
      </div>

      {/* Overall Status */}
      <div className={`mb-6 p-4 rounded-lg ${overallStatus === 'ready' ? 'bg-green-50 border border-green-200' :
          overallStatus === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-yellow-50 border border-yellow-200'
        }`}>
        <div className="flex items-center space-x-3">
          {getStatusIcon(overallStatus === 'ready' ? 'verified' : overallStatus === 'error' ? 'failed' : 'pending')}
          <div>
            <h4 className={`font-medium ${overallStatus === 'ready' ? 'text-green-900' :
                overallStatus === 'error' ? 'text-red-900' :
                  'text-yellow-900'
              }`}>
              {overallStatus === 'ready' ? 'Ready to Send Emails!' :
                overallStatus === 'error' ? 'Setup Issues Detected' :
                  'Setup in Progress'}
            </h4>
            <p className={`text-sm ${overallStatus === 'ready' ? 'text-green-700' :
                overallStatus === 'error' ? 'text-red-700' :
                  'text-yellow-700'
              }`}>
              {overallStatus === 'ready' ? 'Your domain and email are verified. You can start sending emails!' :
                overallStatus === 'error' ? 'Please check the individual statuses below and fix any issues.' :
                  'Please complete the verification steps below.'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Domain Verification */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {getStatusIcon(status.domain)}
            <div>
              <h4 className="font-medium text-gray-900">Domain Verification</h4>
              <p className="text-sm text-gray-600">scaledots.com</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status.domain)}`}>
            {getStatusText(status.domain)}
          </span>
        </div>

        {/* Email Verification */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {getStatusIcon(status.email)}
            <div>
              <h4 className="font-medium text-gray-900">Email Verification</h4>
              <p className="text-sm text-gray-600">info@scubamail.io</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status.email)}`}>
            {getStatusText(status.email)}
          </span>
        </div>

        {/* Production Access */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {getStatusIcon(status.productionAccess)}
            <div>
              <h4 className="font-medium text-gray-900">Production Access</h4>
              <p className="text-sm text-gray-600">Send to any email address</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status.productionAccess)}`}>
            {getStatusText(status.productionAccess)}
          </span>
        </div>

        {status.error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{status.error}</p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-3">Quick Actions:</h4>
          <div className="space-y-2">
            <a
              href="https://console.aws.amazon.com/ses/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              Open AWS SES Console
            </a>
            <a
              href="https://console.aws.amazon.com/ses/v2/home?region=ap-south-1#/verified-identities"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 ml-2"
            >
              Verify Identities
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SESRealTimeChecker;
